import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api, apiErr, apiErrKind } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Lock, ShieldCheck, LogIn, Eye, EyeOff, WifiOff, ServerCrash, Clock, AlertCircle, KeyRound, ArrowLeft } from 'lucide-react';

const ERROR_ICON = {
  timeout: Clock, offline: WifiOff, backend_down: ServerCrash,
  bad_credentials: AlertCircle, rate_limited: Clock, validation: AlertCircle, other: AlertCircle,
};
const ERROR_TITLE = {
  timeout: 'Tiempo de espera agotado', offline: 'Sin conexión', backend_down: 'Servidor no disponible',
  bad_credentials: 'Credenciales incorrectas', rate_limited: 'Demasiados intentos',
  validation: 'Datos inválidos', other: 'No se pudo iniciar sesión',
};

export default function Login() {
  const { login, verify2FA, error, errorKind } = useAuth();
  const [step, setStep] = useState('credentials'); // credentials | totp
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [code, setCode] = useState('');
  const [twoFAErr, setTwoFAErr] = useState('');
  const [health, setHealth] = useState('checking');
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await api.get('/health', { timeout: 8000 }); if (!cancelled) setHealth('ok'); }
      catch { if (!cancelled) setHealth('down'); }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res?.requires_2fa) {
      setChallengeToken(res.challenge_token);
      setStep('totp');
      setCode('');
      setTwoFAErr('');
    } else if (res?.ok && res?.needs_nip_setup) {
      window.dispatchEvent(new CustomEvent('resico:nip-setup-required'));
    }
  };

  const verifyTOTP = async (e) => {
    e.preventDefault();
    setTwoFAErr('');
    setLoading(true);
    const r = await verify2FA(challengeToken, code);
    setLoading(false);
    if (!r?.ok) setTwoFAErr(r?.error || 'Código incorrecto');
    else if (r?.needs_nip_setup) window.dispatchEvent(new CustomEvent('resico:nip-setup-required'));
  };

  const backToCredentials = () => {
    setStep('credentials'); setChallengeToken(''); setCode(''); setTwoFAErr('');
  };

  const Icon = ERROR_ICON[errorKind] || AlertCircle;

  return (
    <div className="min-h-screen w-full flex justify-center bg-background">
      <div className="w-full max-w-md flex flex-col relative safe-top">
        <div className="px-6 pt-14 pb-8">
          <div className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            {step === 'totp' ? <KeyRound className="h-5 w-5 text-primary" strokeWidth={2}/> : <ShieldCheck className="h-5 w-5 text-primary" strokeWidth={2}/>}
          </div>
          <p className="mt-6 text-[10px] uppercase tracking-[0.28em] text-muted-foreground font-semibold">REGIMEN SIMPLIFICADO DE CONFIANZA 626 · MEXICO <span className="text-[#F97316]">{"(BETA)"}</span></p>
          <h1 className="font-display text-4xl font-extrabold tracking-tighter mt-2 leading-[1.05]">
            {step === 'credentials' ? (<>Declaraciones<br/>mensuales</>) : (<>Verificación<br/>de dos factores</>)}
          </h1>
          <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
            {step === 'credentials'
              ? 'Preparación privada de tus estimaciones RESICO. Zona horaria America/Hermosillo.'
              : 'Ingresa el código de 6 dígitos de tu app de autenticación o un código de recuperación.'}
          </p>

          {step === 'credentials' && health === 'down' && (
            <div data-testid="health-warning" className="mt-6 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs flex items-start gap-2">
              <ServerCrash className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400"/>
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-300">Servidor no disponible</p>
                <p className="text-amber-700 dark:text-amber-400">Estamos verificando la conexión. Reintenta en unos segundos.</p>
              </div>
            </div>
          )}
        </div>

        {step === 'credentials' && (
          <form onSubmit={submit} data-testid="login-form" className="px-6 pb-8 space-y-5">
            <div className="space-y-2">
              <Label className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
              <Input
                type="email" inputMode="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="h-12 rounded-lg text-base"
                placeholder="tu@email.com"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Contraseña</Label>
                <button type="button" onClick={() => setForgotOpen(true)} data-testid="btn-forgot-password" className="text-[11px] font-semibold text-primary hover:underline">
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <div className="relative">
                <Input
                  type={show ? 'text' : 'password'} autoComplete="current-password" required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="h-12 rounded-lg text-base pr-12"
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute inset-y-0 right-0 px-3 text-muted-foreground" data-testid="login-toggle-visibility">
                  {show ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                data-error-kind={errorKind}
                className={`rounded-lg border px-3 py-2.5 flex items-start gap-2 text-sm ${errorKind === 'bad_credentials' || errorKind === 'validation' ? 'border-destructive/40 bg-destructive/10 text-destructive' : 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300'}`}
              >
                <Icon className="h-4 w-4 mt-0.5 flex-shrink-0"/>
                <div>
                  <p className="font-semibold" data-testid="login-error-title">{ERROR_TITLE[errorKind] || 'Error'}</p>
                  <p className="opacity-90" data-testid="login-error-detail">{error}</p>
                </div>
              </div>
            )}

            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-12 rounded-full text-base font-semibold">
              {loading ? 'Verificando…' : (<><LogIn className="h-4 w-4 mr-2"/>Iniciar sesión</>)}
            </Button>

            <div className="flex items-start gap-2 mt-6 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0"/>
              <p>Un solo usuario administrador. Sesión con expiración por inactividad. La app no guarda contraseñas del SAT ni e.firma.</p>
            </div>
          </form>
        )}

        {step === 'totp' && (
          <form onSubmit={verifyTOTP} data-testid="totp-form" className="px-6 pb-8 space-y-5">
            <Input
              inputMode="numeric" pattern="[0-9]*" maxLength={11}
              value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
              data-testid="totp-code"
              className="h-14 text-center font-mono-num text-2xl tracking-[0.5em] rounded-lg"
              placeholder="000000"
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground text-center">Puedes ingresar 6 dígitos TOTP o un código de recuperación (ej. A1B2C-3D4E5).</p>
            {twoFAErr && (
              <div data-testid="totp-error" className="text-sm rounded-lg border border-destructive/40 bg-destructive/10 text-destructive px-3 py-2 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0"/>
                <span>{twoFAErr}</span>
              </div>
            )}
            <Button type="submit" disabled={loading || !code} data-testid="totp-submit" className="w-full h-12 rounded-full text-base font-semibold">
              {loading ? 'Verificando…' : 'Verificar código'}
            </Button>
            <button type="button" onClick={backToCredentials} data-testid="totp-back" className="w-full text-sm text-muted-foreground flex items-center justify-center gap-2">
              <ArrowLeft className="h-3.5 w-3.5"/> Volver
            </button>
          </form>
        )}

        <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} defaultEmail={email}/>
      </div>
    </div>
  );
}

function ForgotPasswordDialog({ open, onOpenChange, defaultEmail }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setEmail(defaultEmail || ''); setSent(false); }
  }, [open, defaultEmail]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch {
      // Same generic response — never reveal
      setSent(true);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="forgot-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary"/> Recuperar contraseña</DialogTitle>
          <DialogDescription>
            Enviaremos un enlace de recuperación al correo del administrador registrado. Expira en 1 hora y solo puede usarse una vez.
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div data-testid="forgot-sent" className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm">
            Si el correo está registrado, recibirás un enlace de recuperación en unos minutos. Revisa también la carpeta de spam.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Correo</Label>
              <Input type="email" inputMode="email" required data-testid="forgot-email" className="h-12 mt-1" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com"/>
            </div>
            <Button type="submit" disabled={loading || !email} data-testid="forgot-submit" className="w-full h-11 rounded-full">
              {loading ? 'Enviando…' : 'Enviar enlace'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
